import { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import {
  Loader2, Building2, Mail, Phone, MapPin, Trash2, Download, Search,
  Check, X, Clock, Pause, ChevronDown, ChevronUp, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  fetchWholesaleApplications,
  updateWholesaleApplication,
  deleteWholesaleApplication,
  exportWholesaleApplicationsCsvUrl,
  bulkWholesaleApplications,
} from '@/lib/api';

const STATUSES = [
  { slug: 'all',             label: 'All' },
  { slug: 'pending_review',  label: 'Pending' },
  { slug: 'approved',        label: 'Approved' },
  { slug: 'on_hold',         label: 'On hold' },
  { slug: 'rejected',        label: 'Rejected' },
];

const TIERS = [
  { v: 'all',        l: 'All tiers' },
  { v: 'boutique',   l: 'Boutique' },
  { v: 'growth',     l: 'Growth' },
  { v: 'enterprise', l: 'Enterprise' },
];

const TERMS_OPTIONS = [
  { v: 'cod',    l: 'COD' },
  { v: '7-day',  l: '7-day' },
  { v: '14-day', l: '14-day' },
  { v: '30-day', l: '30-day' },
];

const STATUS_COLOR = {
  pending_review: 'border-amber-400/60 text-amber-500 bg-amber-50',
  approved:       'border-emerald-500/60 text-emerald-600 bg-emerald-50',
  on_hold:        'border-sky-400/60 text-sky-600 bg-sky-50',
  rejected:       'border-red-400/60 text-red-600 bg-red-50',
};

const STATUS_ICON = {
  pending_review: Clock,
  approved: Check,
  on_hold: Pause,
  rejected: X,
};

const STATUS_LABEL = {
  pending_review: 'Pending review',
  approved: 'Approved',
  on_hold: 'On hold',
  rejected: 'Rejected',
};


export default function WholesaleApplicationsPanel() {
  const [status, setStatus] = useState('all');
  const [tier, setTier] = useState('all');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [bulkManager, setBulkManager] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const { data, isLoading, mutate } = useSWR(
    ['admin-wholesale-apps', status, tier],
    ([, s, t]) => fetchWholesaleApplications({ status: s, tier: t }),
    { keepPreviousData: true },
  );

  const apps = data || [];

  const filtered = useMemo(() => {
    if (!query.trim()) return apps;
    const q = query.toLowerCase();
    return apps.filter(
      (a) =>
        a.business_name?.toLowerCase().includes(q) ||
        a.contact_name?.toLowerCase().includes(q) ||
        a.email?.toLowerCase().includes(q) ||
        a.abn?.toLowerCase().includes(q),
    );
  }, [apps, query]);

  const counts = useMemo(() => {
    const c = { all: apps.length };
    apps.forEach((a) => { c[a.status] = (c[a.status] || 0) + 1; });
    return c;
  }, [apps]);

  // Prune selection when the visible set changes so we never bulk-act on hidden rows.
  useEffect(() => {
    const visibleIds = new Set(filtered.map((a) => a.id));
    setSelected((prev) => {
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (visibleIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [filtered]);

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allVisibleSelected = filtered.length > 0 && filtered.every((a) => selected.has(a.id));
  const someVisibleSelected = filtered.some((a) => selected.has(a.id));

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        filtered.forEach((a) => next.delete(a.id));
      } else {
        filtered.forEach((a) => next.add(a.id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action) => {
    if (selected.size === 0) return;
    const label = { approved: 'approve', rejected: 'reject', on_hold: 'put on hold', delete: 'delete' }[action] || action;
    if (action === 'delete' && !window.confirm(`Delete ${selected.size} application(s)? This cannot be undone.`)) return;
    if (action === 'approved' && !window.confirm(
      `Approve ${selected.size} application(s) using each applicant's requested tier and terms?`
      + (bulkManager ? `\n\nAll will be assigned to: ${bulkManager}` : '')
    )) return;
    setBulkBusy(true);
    try {
      const payload = {
        ids: Array.from(selected),
        action,
      };
      if (bulkManager && action === 'approved') payload.assigned_manager = bulkManager;
      const result = await bulkWholesaleApplications(payload);
      const emailNote = result.emails_sent
        ? ` · ${result.emails_sent} welcome email${result.emails_sent === 1 ? '' : 's'} sent`
        : '';
      toast.success(`${result.updated} ${label}${result.updated === 1 ? '' : 's'} done${emailNote}`);
      if (result.failed?.length) {
        toast.error(`${result.failed.length} failed — check console`);
        console.warn('Bulk failures:', result.failed);
      }
      clearSelection();
      setBulkManager('');
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Bulk ${label} failed`);
    } finally {
      setBulkBusy(false);
    }
  };

  const onUpdate = async (id, updates) => {
    try {
      const doc = await updateWholesaleApplication(id, updates);
      const wasApproval = updates.status === 'approved';
      toast.success(wasApproval ? 'Approved · welcome email sent' : 'Application updated');
      mutate();
      return doc;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    }
  };

  const onDelete = async (id, businessName) => {
    if (!window.confirm(`Delete the application from "${businessName}"? This cannot be undone.`)) return;
    try {
      await deleteWholesaleApplication(id);
      toast.success('Application deleted');
      mutate();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div data-testid="wholesale-apps-panel" className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl leading-none tracking-tight">Wholesale applications</h2>
          <p className="text-sm text-white/55 mt-1">
            Trade credit applications submitted via <span className="text-white/80">/wholesale/apply</span>.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={exportWholesaleApplicationsCsvUrl()}
            data-testid="wholesale-apps-export-csv"
            className="inline-flex items-center gap-2 border border-white/20 hover:border-white text-white text-[11px] uppercase tracking-[0.22em] px-4 py-2.5 transition-colors"
          >
            <Download size={14} /> CSV export
          </a>
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        {/* Status pills */}
        <div className="flex flex-wrap gap-2">
          {STATUSES.map((s) => {
            const active = status === s.slug;
            const count = counts[s.slug] ?? (s.slug === 'all' ? apps.length : 0);
            return (
              <button
                key={s.slug}
                type="button"
                onClick={() => setStatus(s.slug)}
                data-testid={`wholesale-apps-filter-${s.slug}`}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                  active
                    ? 'border-[var(--mw-primary)] text-white bg-[var(--mw-primary)]/15'
                    : 'border-white/15 text-white/60 hover:text-white hover:border-white/40'
                }`}
              >
                {s.label}
                <span className="text-white/40 tabular-nums">{count}</span>
              </button>
            );
          })}
        </div>

        {/* Tier dropdown */}
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          data-testid="wholesale-apps-filter-tier"
          className="mw-input w-auto text-xs uppercase tracking-[0.16em] cursor-pointer"
        >
          {TIERS.map((t) => (
            <option key={t.v} value={t.v} className="bg-[var(--mw-surface)]">{t.l}</option>
          ))}
        </select>

        {/* Search */}
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            type="search"
            placeholder="Search business, ABN, email…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="wholesale-apps-search"
            className="mw-input pl-9"
          />
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          data-testid="wholesale-apps-bulk-bar"
          className="sticky top-20 z-20 bg-[var(--mw-primary)]/10 border border-[var(--mw-primary)]/40 px-5 py-3 flex flex-col md:flex-row md:items-center gap-3"
        >
          <div className="flex items-center gap-3 flex-1">
            <span
              data-testid="wholesale-apps-bulk-count"
              className="font-display text-lg text-white leading-none"
            >
              {selected.size} selected
            </span>
            <button
              type="button"
              onClick={clearSelection}
              data-testid="wholesale-apps-bulk-clear"
              className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-white transition-colors"
            >
              Clear
            </button>
            <input
              type="text"
              value={bulkManager}
              onChange={(e) => setBulkManager(e.target.value)}
              placeholder="Assign manager (optional)"
              maxLength={120}
              data-testid="wholesale-apps-bulk-manager"
              className="mw-input flex-1 max-w-[240px] text-xs"
            />
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              disabled={bulkBusy}
              onClick={() => runBulk('approved')}
              data-testid="wholesale-apps-bulk-approve"
              style={{ color: '#fff' }}
              className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
            >
              {bulkBusy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Approve all
            </button>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={() => runBulk('on_hold')}
              data-testid="wholesale-apps-bulk-hold"
              className="inline-flex items-center gap-1.5 border border-sky-400/60 text-sky-500 hover:bg-sky-50 disabled:opacity-50 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
            >
              <Pause size={13} /> Hold
            </button>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={() => runBulk('rejected')}
              data-testid="wholesale-apps-bulk-reject"
              className="inline-flex items-center gap-1.5 border border-red-400/60 text-red-500 hover:bg-red-50 disabled:opacity-50 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
            >
              <X size={13} /> Reject
            </button>
            <button
              type="button"
              disabled={bulkBusy}
              onClick={() => runBulk('delete')}
              data-testid="wholesale-apps-bulk-delete"
              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-red-400 hover:text-red-500 text-white/55 disabled:opacity-50 text-[11px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex items-center gap-2 text-white/50 py-16 justify-center">
          <Loader2 size={16} className="animate-spin" /> Loading applications…
        </div>
      ) : filtered.length === 0 ? (
        <div data-testid="wholesale-apps-empty" className="text-center py-16 text-white/50">
          <FileText size={28} className="mx-auto mb-3 opacity-40" />
          No applications match those filters yet.
        </div>
      ) : (
        <>
          {/* Select-all row */}
          <label
            className="flex items-center gap-3 px-5 py-2.5 text-[10px] uppercase tracking-[0.22em] text-white/50 border border-white/10 border-b-0 bg-white/[0.02] cursor-pointer hover:text-white transition-colors"
          >
            <input
              type="checkbox"
              checked={allVisibleSelected}
              ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
              onChange={toggleAllVisible}
              data-testid="wholesale-apps-select-all"
              className="w-4 h-4 accent-[var(--mw-primary)] cursor-pointer"
            />
            Select all {filtered.length} on this view
          </label>

          <ul data-testid="wholesale-apps-list" className="space-y-3">
            {filtered.map((a) => (
              <ApplicationRow
                key={a.id}
                app={a}
                selected={selected.has(a.id)}
                onSelectToggle={() => toggleOne(a.id)}
                onUpdate={onUpdate}
                onDelete={onDelete}
              />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}


function ApplicationRow({ app, selected, onSelectToggle, onUpdate, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const [approvedTier, setApprovedTier] = useState(app.approved_tier || app.tier_preference || 'growth');
  const [approvedTerms, setApprovedTerms] = useState(app.approved_terms || app.trading_terms_preference || 'cod');
  const [assignedManager, setAssignedManager] = useState(app.assigned_manager || '');
  const [adminNotes, setAdminNotes] = useState(app.admin_notes || '');

  const Icon = STATUS_ICON[app.status] || Clock;
  const badge = STATUS_COLOR[app.status] || STATUS_COLOR.pending_review;
  const submittedAt = app.created_at ? new Date(app.created_at).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  }) : '—';

  // Days-waiting SLA nudge — only for pending review or on-hold rows
  const waitingBadge = useMemo(() => {
    if (!app.created_at || !['pending_review', 'on_hold'].includes(app.status)) return null;
    const created = new Date(app.created_at).getTime();
    const days = Math.floor((Date.now() - created) / 86400000);
    if (days < 1) return { label: 'new today', tone: 'ok' };
    if (days < 2) return { label: '1d waiting', tone: 'ok' };
    if (days < 3) return { label: `${days}d waiting`, tone: 'watch' };
    if (days < 7) return { label: `${days}d waiting`, tone: 'warn' };
    return { label: `${days}d waiting`, tone: 'alert' };
  }, [app.created_at, app.status]);

  const waitingTone = {
    ok:    'border-white/15 text-white/55',
    watch: 'border-amber-400/50 text-amber-500',
    warn:  'border-orange-400/60 text-orange-500',
    alert: 'border-red-500/70 text-red-500 animate-pulse',
  };

  return (
    <li
      data-testid={`wholesale-app-${app.id}`}
      className={`border transition-colors ${
        selected
          ? 'border-[var(--mw-primary)]/50 bg-[var(--mw-primary)]/[0.04]'
          : 'border-white/10 bg-white/[0.02]'
      }`}
    >
      {/* Row summary */}
      <div className="w-full text-left px-5 py-4 flex items-center gap-4">
        <label
          onClick={(e) => e.stopPropagation()}
          className="flex items-center shrink-0 cursor-pointer"
        >
          <input
            type="checkbox"
            checked={!!selected}
            onChange={onSelectToggle}
            data-testid={`wholesale-app-select-${app.id}`}
            className="w-4 h-4 accent-[var(--mw-primary)] cursor-pointer"
          />
        </label>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`wholesale-app-expand-${app.id}`}
          className="flex-1 flex items-center justify-between gap-4 hover:opacity-90"
        >
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className={`shrink-0 inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] ${badge}`}>
              <Icon size={11} />
              {STATUS_LABEL[app.status] || app.status}
            </div>
            {waitingBadge && (
              <div
                data-testid={`wholesale-app-waiting-${app.id}`}
                className={`shrink-0 inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] bg-transparent ${waitingTone[waitingBadge.tone]}`}
                title={`Submitted ${submittedAt}`}
              >
                <Clock size={10} />
                {waitingBadge.label}
              </div>
            )}
            <div className="min-w-0 text-left">
              <div className="font-display text-lg leading-tight truncate">
                {app.business_name}
                <span className="text-white/45 text-sm ml-2 font-sans">
                  · {app.contact_name}
                </span>
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-white/45 flex flex-wrap items-center gap-2">
                <span>{app.tier_preference}</span>
                <span>·</span>
                <span>{app.estimated_volume}</span>
                <span>·</span>
                <span>{app.trading_terms_preference?.toUpperCase()}</span>
                <span>·</span>
                <span>ABN {app.abn}</span>
              </div>
            </div>
          </div>
          <div className="shrink-0 flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] text-white/45">
            <span className="hidden sm:inline">{submittedAt}</span>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </button>
      </div>

      {/* Expanded detail + actions */}
      {expanded && (
        <div className="border-t border-white/10 px-5 py-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left — application detail */}
          <div className="space-y-4">
            <SectionTitle>Applicant details</SectionTitle>
            <KV label="Business type" value={app.business_type} />
            <KV label="Trading since" value={app.trading_since} />
            <KV label="Role" value={app.contact_role} />
            <KV
              label="Contact"
              value={
                <div className="flex flex-col gap-1">
                  <a href={`mailto:${app.email}`} className="inline-flex items-center gap-2 text-white/80 hover:text-[var(--mw-primary)] transition-colors">
                    <Mail size={12} /> {app.email}
                  </a>
                  <a href={`tel:${app.phone}`} className="inline-flex items-center gap-2 text-white/80 hover:text-[var(--mw-primary)] transition-colors">
                    <Phone size={12} /> {app.phone}
                  </a>
                </div>
              }
            />
            <KV
              label="Address"
              value={
                <span className="inline-flex items-start gap-2 text-white/80">
                  <MapPin size={12} className="mt-1 shrink-0" />
                  <span>
                    {app.business_address}
                    {app.delivery_suburb && (
                      <><br />{app.delivery_suburb}, {app.delivery_state} {app.delivery_postcode}</>
                    )}
                  </span>
                </span>
              }
            />
            <KV label="Delivery preference" value={app.delivery_preference} />
            <KV label="Current supplier" value={app.current_supplier} />
            <KV label="Account manager pref." value={
              app.account_manager_name
                ? `${app.account_manager_preference} — ${app.account_manager_name}`
                : app.account_manager_preference
            } />
            {app.reference_1 && <KV label="Reference 1" value={app.reference_1} />}
            {app.reference_2 && <KV label="Reference 2" value={app.reference_2} />}
            {app.notes && <KV label="Notes" value={<span className="whitespace-pre-wrap">{app.notes}</span>} />}
          </div>

          {/* Right — review + decision */}
          <div className="space-y-4">
            <SectionTitle>Review &amp; decision</SectionTitle>

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">Approved tier</div>
              <select value={approvedTier} onChange={(e) => setApprovedTier(e.target.value)} className="mw-input cursor-pointer" data-testid={`wholesale-app-tier-${app.id}`}>
                {TIERS.slice(1).map((t) => (
                  <option key={t.v} value={t.v} className="bg-[var(--mw-surface)]">{t.l}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">Approved trading terms</div>
              <select value={approvedTerms} onChange={(e) => setApprovedTerms(e.target.value)} className="mw-input cursor-pointer" data-testid={`wholesale-app-terms-${app.id}`}>
                {TERMS_OPTIONS.map((t) => (
                  <option key={t.v} value={t.v} className="bg-[var(--mw-surface)]">{t.l}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">Assigned account manager</div>
              <input
                value={assignedManager}
                onChange={(e) => setAssignedManager(e.target.value)}
                placeholder="Name of the team member handling this account"
                maxLength={120}
                data-testid={`wholesale-app-manager-${app.id}`}
                className="mw-input"
              />
            </label>

            <label className="block">
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1.5">Internal notes</div>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                maxLength={2000}
                rows={3}
                data-testid={`wholesale-app-notes-${app.id}`}
                className="mw-input resize-none"
                placeholder="Notes for the team — not shown to the applicant."
              />
            </label>

            <div className="flex flex-wrap gap-2 pt-2">
              <button
                type="button"
                onClick={() => onUpdate(app.id, {
                  status: 'approved',
                  approved_tier: approvedTier,
                  approved_terms: approvedTerms,
                  assigned_manager: assignedManager || null,
                  admin_notes: adminNotes || null,
                })}
                data-testid={`wholesale-app-approve-${app.id}`}
                style={{ color: '#fff' }}
                className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
              >
                <Check size={13} /> Approve
              </button>
              <button
                type="button"
                onClick={() => onUpdate(app.id, {
                  status: 'on_hold',
                  admin_notes: adminNotes || null,
                })}
                data-testid={`wholesale-app-hold-${app.id}`}
                className="inline-flex items-center gap-1.5 border border-sky-400/60 text-sky-500 hover:bg-sky-50 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
              >
                <Pause size={13} /> On hold
              </button>
              <button
                type="button"
                onClick={() => onUpdate(app.id, {
                  status: 'rejected',
                  admin_notes: adminNotes || null,
                })}
                data-testid={`wholesale-app-reject-${app.id}`}
                className="inline-flex items-center gap-1.5 border border-red-400/60 text-red-500 hover:bg-red-50 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
              >
                <X size={13} /> Reject
              </button>
              <button
                type="button"
                onClick={() => onUpdate(app.id, {
                  status: 'pending_review',
                  admin_notes: adminNotes || null,
                })}
                data-testid={`wholesale-app-pending-${app.id}`}
                className="inline-flex items-center gap-1.5 border border-amber-400/60 text-amber-500 hover:bg-amber-50 text-[11px] uppercase tracking-[0.22em] px-4 py-2 transition-colors"
              >
                <Clock size={13} /> Reopen
              </button>
              <div className="flex-1" />
              <button
                type="button"
                onClick={() => onDelete(app.id, app.business_name)}
                data-testid={`wholesale-app-delete-${app.id}`}
                className="inline-flex items-center gap-1.5 border border-white/15 hover:border-red-400 hover:text-red-500 text-white/55 text-[11px] uppercase tracking-[0.22em] px-3 py-2 transition-colors"
              >
                <Trash2 size={13} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </li>
  );
}


function SectionTitle({ children }) {
  return (
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/45 pb-2 border-b border-white/10">
      <Building2 size={12} /> {children}
    </div>
  );
}


function KV({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-sm">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-1">{label}</div>
      <div className="text-white/85 leading-snug">{value}</div>
    </div>
  );
}
